import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Textarea } from '../ui/textarea';

interface PartnerModalProps {
    isOpen: boolean;
    onClose: () => void;
}

interface PartnerFormData {
    institution_name: string;
    contact_name: string;
    email: string;
    phone: string;
    student_count: string;
    message: string;
}

export const PartnerModal = ({ isOpen, onClose }: PartnerModalProps) => {
    const { t, i18n } = useTranslation();
    const [formData, setFormData] = useState<PartnerFormData>({
        institution_name: '',
        contact_name: '',
        email: '',
        phone: '',
        student_count: '',
        message: '',
    });
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const handleChange = (field: keyof PartnerFormData, value: string) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setError('');
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        // Validate required fields
        if (!formData.institution_name || !formData.contact_name || !formData.email) {
            setError(t('partner.errorRequired'));
            setIsLoading(false);
            return;
        }

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/partner/contact`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ ...formData, language: i18n.language }),
            });

            const data = await response.json();

            if (!response.ok) {
                setError(data.error || t('partner.errorGeneric'));
                return;
            }

            setIsSubmitted(true);
        } catch (err) {
            setError(t('partner.errorConnection'));
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 bg-black transition-opacity duration-500 ease-in-out flex items-center justify-center z-50"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
            onClick={onClose}
        >
            <div
                className={`bg-white p-8 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto transform transition-all duration-500 ease-out
          ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'}`}
                onClick={(e) => e.stopPropagation()}
            >
                {!isSubmitted ? (
                    <div className="animate-fadeIn">
                        <h2 className="text-2xl font-bold mb-4" style={{ color: '#00c4cc' }}>
                            {t('partner.title')}
                        </h2>
                        <p className="text-gray-600 mb-6">
                            {t('partner.description')}
                        </p>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('partner.institutionName')} <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    type="text"
                                    placeholder={t('partner.institutionNamePlaceholder')}
                                    value={formData.institution_name}
                                    onChange={(e) => handleChange('institution_name', e.target.value)}
                                    className={error && !formData.institution_name ? 'border-red-500 focus:ring-red-500' : ''}
                                    disabled={isLoading}
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('partner.contactName')} <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    type="text"
                                    placeholder={t('partner.contactNamePlaceholder')}
                                    value={formData.contact_name}
                                    onChange={(e) => handleChange('contact_name', e.target.value)}
                                    className={error && !formData.contact_name ? 'border-red-500 focus:ring-red-500' : ''}
                                    disabled={isLoading}
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('partner.email')} <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    type="email"
                                    placeholder={t('auth.emailPlaceholder')}
                                    value={formData.email}
                                    onChange={(e) => handleChange('email', e.target.value)}
                                    className={error && !formData.email ? 'border-red-500 focus:ring-red-500' : ''}
                                    disabled={isLoading}
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('partner.phone')}
                                </label>
                                <Input
                                    type="tel"
                                    placeholder={t('partner.phonePlaceholder')}
                                    value={formData.phone}
                                    onChange={(e) => handleChange('phone', e.target.value)}
                                    disabled={isLoading}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('partner.studentCount')}
                                </label>
                                <Input
                                    type="text"
                                    placeholder={t('partner.studentCountPlaceholder')}
                                    value={formData.student_count}
                                    onChange={(e) => handleChange('student_count', e.target.value)}
                                    disabled={isLoading}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('partner.message')}
                                </label>
                                <Textarea
                                    placeholder={t('partner.messagePlaceholder')}
                                    value={formData.message}
                                    onChange={(e) => handleChange('message', e.target.value)}
                                    rows={4}
                                    disabled={isLoading}
                                />
                            </div>

                            {error && (
                                <p className="text-red-500 text-sm animate-fadeIn">
                                    {error}
                                </p>
                            )}

                            <div className="flex gap-4">
                                <Button
                                    type="submit"
                                    className="flex-1 text-white transform transition-transform duration-300 hover:scale-102"
                                    style={{ backgroundColor: '#00c4cc' }}
                                    disabled={isLoading}
                                >
                                    {isLoading ? (
                                        <span className="flex items-center justify-center">
                                            <span className="mr-2">{t('partner.submitting')}</span>
                                        </span>
                                    ) : (
                                        t('partner.submit')
                                    )}
                                </Button>
                                <Button
                                    type="button"
                                    onClick={onClose}
                                    variant="outline"
                                    disabled={isLoading}
                                >
                                    {t('partner.cancel')}
                                </Button>
                            </div>
                        </form>
                    </div>
                ) : (
                    <div className="animate-fadeIn">
                        <h2 className="text-2xl font-bold mb-4" style={{ color: '#00c4cc' }}>
                            {t('partner.successTitle')}
                        </h2>
                        <p className="text-gray-600 mb-6">
                            {t('partner.successText')}
                        </p>
                        <Button
                            onClick={onClose}
                            className="w-full transform transition-transform duration-300 hover:scale-102"
                            style={{ backgroundColor: '#00c4cc', color: 'white' }}
                        >
                            {t('partner.close')}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};

